import { ArrowLeft, Clock, Info, Loader2, RotateCcw, Send } from 'lucide-react';
import { useState } from 'react';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';

import { useCouponEvent, useRetryFailedBatch } from '@/features/coupon';
import { PerformanceFunnelCard } from '@/features/coupon-performance/ui/performance-funnel-card';
import { testIds } from '@/shared/testing';
import { SEGMENT_LABELS, SEGMENT_SOFT_COLORS, SEGMENT_TEXT_COLORS } from '@/features/rfm';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { EmptyState } from '@/shared/ui/empty-state';
import { Separator } from '@/shared/ui/separator';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';
import { AppLayout } from '../-components/app-layout';
import { BatchIssueRecords } from './-components/batch-issue-records';
import { BATCH_STATUS_CONFIG, BENEFIT_TYPE_LABELS } from './-components/coupon-detail-constants';
import { NewBatchForm } from './-components/issue-coupon-dialog';
import { SyncStatusBanner } from './-components/sync-status-banner';

import type {
  Cafe24CouponDetailDto,
  CouponIssueBatchDto,
  RfmSegment,
} from '@cafe24-apps/traffic-light-server/contracts';

export const Route = createFileRoute('/_authenticated/$mallId/coupon/$eventId')({
  component: CouponDetailPage,
});

// ============================================
// 쿠폰 정보 카드
// ============================================

function CouponInfoCard({
  cafe24Coupon,
  event,
}: {
  cafe24Coupon: Cafe24CouponDetailDto | null;
  event: { cafe24CouponNo: string; cafe24CouponName: string };
}) {
  const couponName = cafe24Coupon?.coupon_name ?? event.cafe24CouponName;
  const benefitType = cafe24Coupon
    ? (BENEFIT_TYPE_LABELS[cafe24Coupon.benefit_type] ?? cafe24Coupon.benefit_type)
    : '-';
  const benefitText = cafe24Coupon?.benefit_text ?? '-';
  const period =
    cafe24Coupon?.available_begin_datetime && cafe24Coupon?.available_end_datetime
      ? `${new Date(cafe24Coupon.available_begin_datetime).toLocaleDateString('ko-KR')} ~ ${new Date(cafe24Coupon.available_end_datetime).toLocaleDateString('ko-KR')}`
      : cafe24Coupon?.available_begin_datetime
        ? `${new Date(cafe24Coupon.available_begin_datetime).toLocaleDateString('ko-KR')} ~`
        : '-';
  const issuedCount = cafe24Coupon?.issued_count ?? 0;
  const maxCount = cafe24Coupon?.issue_max_count ?? 0;

  return (
    <Card className="py-0 gap-0">
      <CardHeader className="border-b py-4">
        <CardTitle className="text-sm flex items-center gap-2">
          <Info className="h-4 w-4 text-muted-foreground" />
          쿠폰 정보
        </CardTitle>
      </CardHeader>
      <CardContent className="py-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">쿠폰명</p>
            <p className="text-sm font-medium truncate">{couponName}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">혜택</p>
            <p className="text-sm font-medium">
              {benefitType} {benefitText !== '-' && `(${benefitText})`}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">사용기간</p>
            <p className="text-sm font-medium">{period}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">발급 현황 (Cafe24)</p>
            <p className="text-sm font-medium">
              {issuedCount.toLocaleString()}
              {maxCount > 0 && ` / ${maxCount.toLocaleString()}`}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// 배치 대상 요약
// ============================================

function BatchTargetSummary({ batch }: { batch: CouponIssueBatchDto }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {batch.targetSegments.map((seg) => (
        <span
          key={seg}
          className="px-2 py-0.5 rounded-full text-xs font-medium"
          style={{
            background: SEGMENT_SOFT_COLORS[seg as RfmSegment],
            color: SEGMENT_TEXT_COLORS[seg as RfmSegment],
          }}
        >
          {SEGMENT_LABELS[seg as RfmSegment]}
        </span>
      ))}
      {batch.individualMemberIds.length > 0 && (
        <Badge variant="secondary" className="text-xs">
          +{batch.individualMemberIds.length}명
        </Badge>
      )}
      {batch.targetSegments.length === 0 && batch.individualMemberIds.length === 0 && (
        <span className="text-muted-foreground text-xs">-</span>
      )}
    </div>
  );
}

// ============================================
// Main Page
// ============================================

function CouponDetailPage() {
  const { mallId, eventId } = useParams({ from: '/_authenticated/$mallId/coupon/$eventId' });
  const { data, isLoading } = useCouponEvent(eventId!);
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [recordsModalBatchId, setRecordsModalBatchId] = useState<string | null>(null);
  const [retryTargetBatch, setRetryTargetBatch] = useState<{
    id: string;
    failedCount: number;
  } | null>(null);
  const retryMutation = useRetryFailedBatch();

  const event = data?.event;
  const batches = data?.batches ?? [];
  const cafe24Coupon = data?.cafe24Coupon ?? null;
  const syncStatus = data?.syncStatus ?? 'ok';
  const retriedBatchIds = new Set(
    batches.filter((b) => b.retryOfBatchId).map((b) => b.retryOfBatchId!),
  );

  if (isLoading) {
    return (
      <AppLayout pageTitle="쿠폰 이벤트">
        <div className="space-y-6 max-w-5xl mx-auto">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-12 w-full rounded-xl" />
          <Skeleton className="h-40 w-full rounded-xl" />
          <Skeleton className="h-60 w-full rounded-xl" />
        </div>
      </AppLayout>
    );
  }

  if (!event) {
    return (
      <AppLayout pageTitle="쿠폰 이벤트">
        <div className="text-center py-16">
          <p className="text-sm text-muted-foreground mb-3">이벤트를 찾을 수 없습니다</p>
          <Button variant="outline" size="sm" asChild>
            <Link to="/$mallId/coupon" params={{ mallId: mallId }}>
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              목록으로 돌아가기
            </Link>
          </Button>
        </div>
      </AppLayout>
    );
  }

  const isUnavailable =
    syncStatus === 'deleted' || syncStatus === 'expired' || syncStatus === 'stopped';

  // 통계 계산
  const batchCount = batches.length;
  const totalIssued = batches.reduce((sum, b) => sum + b.totalCount, 0);
  const totalSuccess = batches.reduce((sum, b) => sum + b.successCount, 0);
  const latestIssuedAt = batches
    .filter((b) => b.issuedAt)
    .sort((a, b) => new Date(b.issuedAt!).getTime() - new Date(a.issuedAt!).getTime())[0]?.issuedAt;

  return (
    <AppLayout pageTitle="쿠폰 이벤트">
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* 헤더 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-muted-foreground"
              asChild
              data-testid={testIds.coupon.backButton}
            >
              <Link to="/$mallId/coupon" params={{ mallId: mallId }}>
                <ArrowLeft className="h-4 w-4" />
                목록
              </Link>
            </Button>
            <Separator orientation="vertical" className="h-5" />
            <div>
              <h1 className="text-lg font-semibold" data-testid={testIds.coupon.eventName}>
                {event.name}
              </h1>
              <p className="text-xs mt-0.5 text-muted-foreground flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                {new Date(event.createdAt).toLocaleDateString('ko-KR')} 생성
              </p>
            </div>
          </div>
          <Dialog open={issueModalOpen} onOpenChange={setIssueModalOpen}>
            <DialogTrigger asChild>
              <Button
                className="gap-1.5"
                disabled={isUnavailable}
                data-testid={testIds.coupon.newIssueButton}
              >
                <Send className="h-4 w-4" />새 발급
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>새 쿠폰 발급</DialogTitle>
                <DialogDescription>대상을 선택하고 쿠폰을 발급합니다</DialogDescription>
              </DialogHeader>
              <NewBatchForm eventId={eventId!} onClose={() => setIssueModalOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>

        {/* 상태 알림 배너 */}
        <SyncStatusBanner syncStatus={syncStatus} cafe24Coupon={cafe24Coupon} />

        {/* 쿠폰 정보 카드 */}
        <CouponInfoCard cafe24Coupon={cafe24Coupon} event={event} />

        {/* 서머리 카드 */}
        {batches.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="py-4">
              <CardContent className="text-center">
                <p className="text-xs text-muted-foreground">발급 횟수</p>
                <p className="text-2xl font-semibold mt-1">{batchCount}회</p>
              </CardContent>
            </Card>
            <Card className="py-4">
              <CardContent className="text-center">
                <p className="text-xs text-muted-foreground">총 발급 대상</p>
                <p className="text-2xl font-semibold mt-1">{totalIssued.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="py-4">
              <CardContent className="text-center">
                <p className="text-xs text-muted-foreground">발급 고객</p>
                <p className="text-2xl font-semibold mt-1 text-signal-green-text">
                  {totalSuccess.toLocaleString()}
                </p>
              </CardContent>
            </Card>
            <Card className="py-4">
              <CardContent className="text-center">
                <p className="text-xs text-muted-foreground">최근 발급일</p>
                <p className="text-lg font-semibold mt-1">
                  {latestIssuedAt
                    ? new Date(latestIssuedAt).toLocaleDateString('ko-KR', {
                        month: 'short',
                        day: 'numeric',
                      })
                    : '-'}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* 성과 퍼널 카드 */}
        <PerformanceFunnelCard eventId={eventId!} />

        {/* 배치 히스토리 */}
        <Card className="py-0 gap-0">
          <CardHeader className="border-b py-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              발급 히스토리
            </CardTitle>
            {batches.length > 0 && (
              <CardDescription className="text-xs">총 {batches.length}회 발급</CardDescription>
            )}
          </CardHeader>
          {batches.length === 0 ? (
            <EmptyState
              icon={Send}
              title="아직 발급 이력이 없어요"
              description="쿠폰을 발급하면 여기에 히스토리가 쌓여요"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>대상</TableHead>
                  <TableHead>결과</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead>발급일시</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map((batch, idx) => {
                  const config = BATCH_STATUS_CONFIG[batch.status];
                  const successPct =
                    batch.totalCount > 0
                      ? Math.round((batch.successCount / batch.totalCount) * 100)
                      : 0;
                  return (
                    <TableRow
                      key={batch.id}
                      className="cursor-pointer transition-colors hover:bg-accent/50"
                      onClick={() => setRecordsModalBatchId(batch.id)}
                      data-testid={testIds.coupon.batchRow}
                    >
                      <TableCell className="font-medium text-muted-foreground">
                        {batches.length - idx}
                      </TableCell>
                      <TableCell>
                        <BatchTargetSummary batch={batch} />
                      </TableCell>
                      <TableCell>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-2 min-w-[120px]">
                              <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-signal-green transition-all"
                                  style={{ width: `${successPct}%` }}
                                />
                              </div>
                              <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                                {batch.successCount}/{batch.totalCount}
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            성공 {batch.successCount} / 실패 {batch.failedCount} ({successPct}%)
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Badge variant={config.variant}>{config.label}</Badge>
                          {batch.retryOfBatchId && (
                            <Badge variant="outline" className="text-[10px] px-1.5">
                              <RotateCcw className="h-2.5 w-2.5 mr-0.5" />
                              재발급
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {batch.issuedAt ? new Date(batch.issuedAt).toLocaleString('ko-KR') : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {(batch.status === 'failed' || batch.status === 'partial_failed') &&
                            batch.failedCount > 0 &&
                            (retriedBatchIds.has(batch.id) ? (
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 text-muted-foreground"
                                data-testid={testIds.coupon.retriedBadge}
                              >
                                <RotateCcw className="h-2.5 w-2.5 mr-0.5" />
                                재발급됨
                              </Badge>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs"
                                disabled={retryMutation.isPending}
                                data-testid={testIds.coupon.retryButton}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRetryTargetBatch({
                                    id: batch.id,
                                    failedCount: batch.failedCount,
                                  });
                                }}
                              >
                                {retryMutation.isPending &&
                                retryMutation.variables?.batchId === batch.id ? (
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                  <RotateCcw className="h-3 w-3 mr-1" />
                                )}
                                재발급
                              </Button>
                            ))}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            data-testid={testIds.coupon.batchDetailButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              setRecordsModalBatchId(batch.id);
                            }}
                          >
                            상세
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* 발급 내역 상세 모달 */}
        <Dialog
          open={!!recordsModalBatchId}
          onOpenChange={(open) => !open && setRecordsModalBatchId(null)}
        >
          <DialogContent
            className="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
            data-testid={testIds.coupon.recordsModal}
          >
            <DialogHeader>
              <DialogTitle>발급 내역 상세</DialogTitle>
              <DialogDescription>개별 고객의 쿠폰 발급 결과를 확인합니다</DialogDescription>
            </DialogHeader>
            {recordsModalBatchId && <BatchIssueRecords batchId={recordsModalBatchId} />}
          </DialogContent>
        </Dialog>

        <AlertDialog
          open={!!retryTargetBatch}
          onOpenChange={(open) => !open && setRetryTargetBatch(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>쿠폰을 재발급하시겠습니까?</AlertDialogTitle>
              <AlertDialogDescription>
                실패한 {retryTargetBatch?.failedCount}건을 재발급합니다.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>취소</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (retryTargetBatch) {
                    retryMutation.mutate({ eventId: eventId!, batchId: retryTargetBatch.id });
                  }
                }}
              >
                재발급
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppLayout>
  );
}
